import { getOrderAmounts } from '@/utils/orderAmounts';
import { roundNumber } from './rounding';

function toNumber(value, fallback = 0) {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : fallback;
}

function toNullableNumber(value) {
  if (value == null || value === '') return null;
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeServiceType(raw = {}) {
  const type = raw?.serviceType ?? raw?.deliveryType ?? raw?.type ?? raw?.orderType ?? raw?.sourceType ?? 'Local';

  if (typeof type === 'string') {
    const normalized = type.toLowerCase();
    if (['local', 'dine-in', 'dinein', 'restaurant'].includes(normalized)) return 'dine-in';
    if (normalized === 'delivery') return 'delivery';
    if (normalized === 'pickup') return 'pickup';
    return normalized;
  }

  return 'dine-in';
}

function normalizeStatus(raw = {}) {
  const status = raw?.status ?? raw?.paymentState ?? raw?.state ?? 'Draft';
  if (typeof status !== 'string') return 'Draft';

  const normalized = status.toLowerCase();
  if (['paid', 'closed', 'done', 'completed'].includes(normalized)) return 'Paid';
  if (['cancelled', 'canceled', 'rejected'].includes(normalized)) return 'Cancelled';
  if (['created', 'draft', 'inprogress', 'in_progress', 'processing'].includes(normalized)) return 'Draft';
  return status;
}

function extractCheckNumber(raw = {}) {
  return String(
    raw?.checkNumber ?? raw?.number ?? raw?.unicNumber ?? raw?.orderNumber ?? raw?.orderNum ?? raw?.id ?? '',
  );
}

function extractPaidAt(raw = {}) {
  const paidAt = toNumber(raw?.paidAt ?? raw?.updatedAt ?? raw?.createdAt ?? raw?.date, 0);
  if (!paidAt) return 0;
  return paidAt > 9999999999 ? Math.floor(paidAt / 1000) : paidAt;
}

function extractBonusAccrualAmount(raw = {}) {
  return toNumber(raw?.bonusAccrualAmount ?? raw?.accruedBonus ?? 0);
}

function normalizeLine(raw = {}, index = 0) {
  const unitPrice = toNumber(raw?.unitPrice ?? raw?.itemPrice ?? raw?.productPrice ?? raw?.price, 0);
  const qty = toNumber(raw?.qty ?? raw?.quantity ?? raw?.count, 0);

  return {
    lineId: String(raw?.lineId ?? raw?.id ?? raw?.itemUnicId ?? `${index}`),
    productId: raw?.productId ?? raw?.id ?? null,
    sku: raw?.sku ?? raw?.productExternalId ?? raw?.article ?? null,
    productName: raw?.productName ?? raw?.name ?? 'Без названия',
    unitPrice,
    qty,
    lineTotal: toNumber(raw?.lineTotal ?? raw?.sumByLine, unitPrice * qty),
  };
}

function roundMoney(value) {
  return roundNumber(toNumber(value, 0), 2);
}

function getNestedModifiers(item = {}) {
  return [
    ...(Array.isArray(item?.modifiers) ? item.modifiers : []),
    ...(Array.isArray(item?.modificators) ? item.modificators : []),
    ...(Array.isArray(item?.modifications) ? item.modifications : []),
    ...(Array.isArray(item?.productModifiers) ? item.productModifiers : []),
    ...(Array.isArray(item?.orderItemModifiers) ? item.orderItemModifiers : []),
  ];
}

function getOrderItemDiscountTotal(discounts = [], itemId = '', modifierItems = []) {
  const normalizedItemId = String(itemId || '');
  const modifierIds = new Set(modifierItems.map((modifier) => String(modifier?.id ?? '')).filter(Boolean));

  return discounts.reduce((sum, discount) => {
    const orderItemId = String(discount?.orderItemId ?? '');
    const modifierId = String(discount?.itemModifierId ?? discount?.modifierId ?? '');
    const belongsToItem = normalizedItemId && orderItemId === normalizedItemId;
    const belongsToModifier = modifierId && modifierIds.has(modifierId);

    return belongsToItem || belongsToModifier
      ? sum + toNumber(discount?.discountValue ?? discount?.value ?? discount?.amount, 0)
      : sum;
  }, 0);
}

function getOrderItemOwnDiscountTotal(discounts = [], itemId = '') {
  const normalizedItemId = String(itemId || '');
  if (!normalizedItemId) return 0;

  return discounts.reduce((sum, discount) => {
    const orderItemId = String(discount?.orderItemId ?? '');
    const modifierId = String(discount?.itemModifierId ?? discount?.modifierId ?? '');
    if (orderItemId !== normalizedItemId || modifierId) return sum;
    return sum + toNumber(discount?.discountValue ?? discount?.value ?? discount?.amount, 0);
  }, 0);
}

function getModifierDiscountTotal(discounts = [], modifier = {}) {
  const modifierId = String(modifier?.id ?? modifier?.lineId ?? '');
  if (!modifierId) return 0;

  return discounts.reduce((sum, discount) => {
    const orderItemId = String(discount?.orderItemId ?? '');
    const discountModifierId = String(discount?.itemModifierId ?? discount?.modifierId ?? '');
    const belongsToModifier =
      discountModifierId === modifierId || (!discountModifierId && orderItemId === modifierId);

    return belongsToModifier ? sum + toNumber(discount?.discountValue ?? discount?.value ?? discount?.amount, 0) : sum;
  }, 0);
}

function normalizeOrderItemLine(raw = {}, index = 0, options = {}) {
  const qty = toNumber(raw?.qty ?? raw?.quantity ?? raw?.count, 0);
  const unitPrice = toNumber(raw?.unitPrice ?? raw?.itemPrice ?? raw?.productPrice ?? raw?.price, 0);
  const baseLineTotal = toNumber(raw?.lineTotal ?? raw?.sumByLine, unitPrice * qty);
  const discountTotal = toNumber(options.discountTotal, 0);
  const lineTotal = Math.max(0, baseLineTotal - discountTotal);
  const lineId = String(raw?.id ?? raw?.lineId ?? `${index}`);
  const catalogItem = raw?.selectedCatalogItem && typeof raw.selectedCatalogItem === 'object'
    ? raw.selectedCatalogItem
    : null;
  const catalogSku =
    catalogItem?.sku ??
    catalogItem?.productExternalId ??
    catalogItem?.externalId ??
    catalogItem?.article ??
    catalogItem?.id ??
    null;

  return {
    lineId: options.lineId || lineId,
    productId: raw?.productId ?? null,
    sku: raw?.sku ?? raw?.externalId ?? catalogSku ?? raw?.productExternalId ?? raw?.article ?? raw?.productId ?? raw?.id ?? null,
    productName: raw?.productName ?? raw?.name ?? 'Без названия',
    unitPrice: roundMoney(qty ? lineTotal / qty : 0),
    qty,
    lineTotal: roundMoney(lineTotal),
    discountTotal,
    isModifier: Boolean(options.isModifier),
    ...(options.isDisplayOnly ? { isDisplayOnly: true } : {}),
    parentLineId: options.parentLineId || null,
  };
}

function normalizeSizeLine(item = {}, itemId = '') {
  const size = item?.selectedSize;
  if (!size || typeof size !== 'object') return null;

  const sizeId = String(size?.id ?? item?.sizeId ?? '');
  if (!sizeId) return null;
  const sizePrice = roundMoney(size?.price ?? 0);

  return {
    lineId: `${itemId}:size:${sizeId}`,
    productId: item?.productId ?? null,
    sku: sizeId,
    productName: size?.name ?? 'Размер',
    unitPrice: sizePrice,
    qty: '',
    lineTotal: 0,
    discountTotal: 0,
    isModifier: true,
    isDisplayOnly: true,
    parentLineId: itemId,
  };
}

export function normalizeOrderItemsPayload(payload = {}, options = {}) {
  const rawItems = Array.isArray(payload) ? payload : toArray(payload?.orderItems);
  const discounts = Array.isArray(payload?.discounts) ? payload.discounts : [];
  const includeModifiersAsLines = Boolean(options.includeModifiersAsLines);
  const itemIds = new Set(rawItems.map((item) => String(item?.id ?? '')).filter(Boolean));
  const modifiersByItemId = rawItems.reduce((acc, item) => {
    const parentId = String(item?.itemId ?? '');
    const itemId = String(item?.id ?? '');
    if (!parentId || !itemIds.has(parentId) || parentId === itemId) return acc;

    if (!acc[parentId]) acc[parentId] = [];
    acc[parentId].push(item);
    return acc;
  }, {});

  return rawItems
    .filter((item) => {
      const parentId = String(item?.itemId ?? '');
      const itemId = String(item?.id ?? '');
      return !parentId || !itemIds.has(parentId) || parentId === itemId;
    })
    .flatMap((item, index) => {
      const itemId = String(item?.id ?? item?.lineId ?? `${index}`);
      const qty = toNumber(item?.qty ?? item?.quantity ?? item?.count, 0);
      const baseUnitPrice = toNumber(item?.unitPrice ?? item?.itemPrice ?? item?.productPrice ?? item?.price, 0);
      const baseLineTotal = toNumber(item?.lineTotal ?? item?.sumByLine, baseUnitPrice * qty);
      const modifierItems = [...(modifiersByItemId[itemId] || []), ...getNestedModifiers(item)];

      if (includeModifiersAsLines) {
        const parentLine = normalizeOrderItemLine(item, index, {
          discountTotal: getOrderItemOwnDiscountTotal(discounts, itemId),
        });
        const sizeLine = normalizeSizeLine(item, itemId);
        const modifierLines = modifierItems.map((modifier, modifierIndex) => {
          const modifierId = String(modifier?.id ?? modifier?.lineId ?? `${index}-${modifierIndex}`);
          return normalizeOrderItemLine(modifier, `${index}-${modifierIndex}`, {
            lineId: `${itemId}:modifier:${modifierId}`,
            isModifier: true,
            parentLineId: itemId,
            discountTotal: getModifierDiscountTotal(discounts, modifier),
          });
        });

        return [parentLine, ...(sizeLine ? [sizeLine] : []), ...modifierLines];
      }

      const modifiersTotal = modifierItems.reduce((sum, modifier) => {
        const modifierUnitPrice = toNumber(
          modifier?.unitPrice ?? modifier?.itemPrice ?? modifier?.productPrice ?? modifier?.price,
          0,
        );
        const modifierQty = toNumber(modifier?.qty ?? modifier?.quantity ?? modifier?.count, 0);
        return sum + toNumber(modifier?.lineTotal ?? modifier?.sumByLine, modifierUnitPrice * modifierQty);
      }, 0);
      const discountTotal = getOrderItemDiscountTotal(discounts, itemId, modifierItems);
      const lineTotal = Math.max(0, baseLineTotal + modifiersTotal - discountTotal);
      const unitPrice = qty ? lineTotal / qty : 0;

      return {
        lineId: itemId,
        productId: item?.productId ?? null,
        sku: item?.sku ?? item?.productExternalId ?? item?.article ?? null,
        productName: item?.productName ?? item?.name ?? 'Без названия',
        unitPrice: roundMoney(unitPrice),
        qty,
        lineTotal: roundMoney(lineTotal),
        modifiersTotal,
        discountTotal,
        modifiers: modifierItems,
      };
    });
}

function extractItems(raw = {}) {
  if (toArray(raw?.orderItems).length) {
    return normalizeOrderItemsPayload({
      orderItems: raw.orderItems,
      discounts: raw.discounts,
    });
  }

  const items = toArray(raw?.items);
  return items.map((item, index) => normalizeLine(item, index));
}

function extractGrossAmount(amounts, items = []) {
  return amounts.gross || items.reduce((sum, item) => sum + toNumber(item.lineTotal), 0);
}

// legacy `price` is only trusted when no resultSum/netAmount/amountToPaid is given
function extractNetAmount(raw = {}, amounts, grossAmount) {
  if (amounts.hasServerNet) return amounts.net;
  if (raw?.price != null) return toNumber(raw.price, 0);
  return Math.max(0, grossAmount - amounts.discount - amounts.bonus);
}

function extractGuestRating(raw = {}) {
  return toNullableNumber(
    raw?.guestRating ?? raw?.rating ?? raw?.reviewMark ?? raw?.mark ?? raw?.review?.mark ?? raw?.feedback?.mark ?? null,
  );
}

function extractGuestReviewText(raw = {}) {
  return String(
    raw?.guestReviewText ??
      raw?.reviewText ??
      raw?.notes ??
      raw?.text ??
      raw?.review?.text ??
      raw?.feedback?.text ??
      '',
  );
}

export function normalizePoint(point = {}) {
  const id = point?.id ?? point?.pointId ?? point?.key ?? point?.value ?? '';
  return {
    id: String(id || ''),
    title: point?.title ?? point?.name ?? point?.label ?? point?.text ?? point?.pointTitle ?? '—',
  };
}

function toValueArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null || value === '') return [];
  return [value];
}

function extractPointId(point) {
  if (point == null || point === '') return '';
  if (typeof point !== 'object') return String(point);
  return String(point?.id ?? point?.pointId ?? point?.key ?? point?.value ?? point?.point?.id ?? '');
}

function extractPointTitle(point) {
  if (!point || typeof point !== 'object') return '';
  return String(
    point?.title ??
      point?.name ??
      point?.label ??
      point?.text ??
      point?.pointTitle ??
      point?.pointName ??
      point?.point?.title ??
      point?.point?.name ??
      '',
  );
}

function uniqNonEmpty(values = []) {
  return [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))];
}

export function normalizeEmployee(employee = {}) {
  const rawId = employee?.id ?? employee?.employeeId ?? employee?.personalId ?? '';
  const id = String(rawId || '');
  const firstName = employee?.firstName ?? employee?.firstname ?? '';
  const lastName = employee?.lastName ?? employee?.lastname ?? '';
  const points = [
    ...toValueArray(employee?.point),
    ...toValueArray(employee?.points),
    ...toValueArray(employee?.pointList),
    ...toValueArray(employee?.salesPoints),
    ...toValueArray(employee?.attachedPoints),
  ];
  const pointIds = uniqNonEmpty([
    ...points.map((point) => extractPointId(point)),
    ...toValueArray(employee?.pointId),
    ...toValueArray(employee?.pointIds),
    ...toValueArray(employee?.pointsIds),
    ...toValueArray(employee?.pointID),
    ...toValueArray(employee?.point_id),
  ]);
  const pointTitles = uniqNonEmpty([
    ...points.map((point) => extractPointTitle(point)),
    ...toValueArray(employee?.pointTitle),
    ...toValueArray(employee?.pointTitles),
    ...toValueArray(employee?.pointName),
    ...toValueArray(employee?.pointNames),
  ]);
  const fullName =
    employee?.fullName ??
    employee?.employeeName ??
    `${firstName} ${lastName}`.trim() ??
    employee?.name ??
    employee?.fullname ??
    '';

  return {
    id,
    employeeId: id,
    employeeExternalId: employee?.externalPersonalId ?? employee?.employeeExternalId ?? null,
    employeeName: fullName || 'Неизвестный сотрудник',
    firstName,
    lastName,
    pointIds,
    pointTitles,
  };
}

export function normalizeEmployeeCheckRecord(raw = {}, refs = {}) {
  const items = extractItems(raw);
  const amounts = getOrderAmounts(raw);
  const { discount: discountAmount, bonus: bonusWriteOffAmount } = amounts;
  const grossAmount = extractGrossAmount(amounts, items);
  const netAmount = extractNetAmount(raw, amounts, grossAmount);
  const pointId = String(raw?.pointId ?? raw?.point?.id ?? '');
  const employeeId = String(raw?.employeeId ?? raw?.personalId ?? raw?.employee?.id ?? '');
  const pointTitle = raw?.pointTitle ?? raw?.point?.title ?? refs.pointsById?.[pointId]?.title ?? '—';
  const employeeName =
    raw?.employeeName ??
    raw?.personalName ??
    raw?.employee?.employeeName ??
    refs.employeesById?.[employeeId]?.employeeName ??
    'Неизвестный сотрудник';
  const tableIdRaw = raw?.tableId ?? raw?.table?.id ?? raw?.table?.tableId ?? null;
  const tableExternalIdRaw = raw?.tableExternalId ?? raw?.table?.externalId ?? null;

  return {
    checkId: String(raw?.checkId ?? raw?.id ?? raw?.orderId ?? ''),
    id: String(raw?.checkId ?? raw?.id ?? raw?.orderId ?? ''),
    orderId: raw?.orderId ?? raw?.id ?? null,
    checkNumber: extractCheckNumber(raw),
    pointId,
    pointTitle,
    employeeId,
    employeeExternalId:
      raw?.employeeExternalId ??
      raw?.externalPersonalId ??
      refs.employeesById?.[employeeId]?.employeeExternalId ??
      null,
    employeeName,
    paidAt: extractPaidAt(raw),
    status: normalizeStatus(raw),
    serviceType: normalizeServiceType(raw),
    tableId: tableIdRaw == null || String(tableIdRaw).trim() === '' ? null : String(tableIdRaw),
    tableExternalId:
      tableExternalIdRaw == null || String(tableExternalIdRaw).trim() === '' ? null : String(tableExternalIdRaw),
    tableNumber: raw?.tableNumber ?? raw?.table?.number ?? raw?.tableExternalId ?? raw?.tableId ?? null,
    grossAmount,
    discountAmount,
    bonusWriteOffAmount,
    bonusAccrualAmount: extractBonusAccrualAmount(raw),
    netAmount,
    guestRating: extractGuestRating(raw),
    guestReviewText: extractGuestReviewText(raw),
    reviewId: raw?.reviewId ?? raw?.review?.id ?? raw?.feedback?.id ?? null,
    items,
    raw,
  };
}

export function buildReferenceMaps({ points = [], employees = [] } = {}) {
  const pointsById = points.reduce((acc, point) => {
    const normalized = normalizePoint(point);
    if (normalized.id) acc[normalized.id] = normalized;
    return acc;
  }, {});

  const employeesById = employees.reduce((acc, employee) => {
    const normalized = normalizeEmployee(employee);
    if (normalized.employeeId) acc[normalized.employeeId] = normalized;
    if (normalized.employeeExternalId) acc[String(normalized.employeeExternalId)] = normalized;
    return acc;
  }, {});

  return {
    pointsById,
    employeesById,
  };
}
